import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import FeatureGate from './FeatureGate';
import FeatureTiers from './FeatureTiers';
import Branding from './Branding';
import OneClickNeoTrainer from './OneClickNeoTrainer';
import ExpertLearningIntelligence from './ExpertLearningIntelligence';
import BountyDashboardWidget from './BountyDashboardWidget';
import FriendTrainingDashboardWidget from './FriendTrainingDashboardWidget';
import SessionHistoryTimeline from './SessionHistoryTimeline';
import CuriosityRadarChart from './CuriosityRadarChart';
import CognitiveStateTrends from './CognitiveStateTrends';
import BloomsTaxonomyFish from './BloomsTaxonomyFish';
import WaterClarityIndicator from './WaterClarityIndicator';
import EcosystemHealthDashboard from './EcosystemHealthDashboard';
import GroupManagement from './GroupManagement';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

function Dashboard({ userTier, hasFeatureAccess, user }) {
  const [sessions, setSessions] = useState([]);
  const [taxonomyData, setTaxonomyData] = useState(null);
  const [adaptationData, setAdaptationData] = useState(null);
  const [latestCognitiveState, setLatestCognitiveState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFeature, setPreviewFeature] = useState(null);

  // Get current user ID dynamically
  const userId = user?.id || user?.user_id || 'unknown';
  const isGuest = !user || userTier === 'guest';

  // Sample preview data for guests (Stripe/Linear/Notion style - showcasing rich analytics)
  const samplePreviewData = {
    sessions: [
      {
        id: 1,
        subject: 'Algebra',
        duration: 1800,
        created_at: new Date().toISOString(),
        state: [
          { role: 'user', content: 'Can you help me understand quadratic equations?' },
          { role: 'assistant', content: 'Of course! Let\'s break down quadratic equations...' },
          { role: 'user', content: 'What about factoring?' },
          { role: 'assistant', content: 'Great question! Factoring is...' },
          { role: 'user', content: 'That makes sense now, thank you!' }
        ],
        cognitive_state: {
          knowledge_level: 0.85,
          engagement_score: 0.92,
          confusion_indicators: 0.08
        },
        interaction_count: 5,
        user_id: 'preview',
        session_id: 'preview-1'
      },
      {
        id: 2,
        subject: 'Chemistry',
        duration: 2100,
        created_at: new Date(Date.now() - 86400000).toISOString(), // Yesterday
        state: [
          { role: 'user', content: 'Explain chemical bonding' },
          { role: 'assistant', content: 'Let\'s explore ionic and covalent bonds...' },
          { role: 'user', content: 'Can you show me examples?' },
          { role: 'assistant', content: 'Absolutely! Here are some...' }
        ],
        cognitive_state: {
          knowledge_level: 0.78,
          engagement_score: 0.88,
          confusion_indicators: 0.12
        },
        interaction_count: 4,
        user_id: 'preview',
        session_id: 'preview-2'
      }
    ],
    taxonomyData: {
      remember: { average_score: 0.88, count: 24 },
      understand: { average_score: 0.82, count: 20 },
      apply: { average_score: 0.76, count: 16 },
      analyze: { average_score: 0.70, count: 12 },
      evaluate: { average_score: 0.64, count: 8 },
      create: { average_score: 0.55, count: 5 }
    }
  };

  useEffect(() => {
    // For guests, show preview data immediately
    if (isGuest) {
      setSessions(samplePreviewData.sessions);
      setTaxonomyData(samplePreviewData.taxonomyData);
      setLoading(false);
      return;
    }

    // Load initial dashboard data for authenticated users
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Fetch sessions data
        const sessionsResponse = await axios.get('/api/sessions');
        if (sessionsResponse.data && sessionsResponse.data.sessions) {
          setSessions(sessionsResponse.data.sessions);
        }
        
        // Fetch latest cognitive state from session history
        if (hasFeatureAccess('BASIC_PROGRESS_TRACKING')) {
          try {
            // Get session history to extract latest cognitive state
            const sessionHistoryResponse = await axios.get(`/api/analytics/session-history/${userId}`);
            if (sessionHistoryResponse.data.success && sessionHistoryResponse.data.sessions.length > 0) {
              const latestSession = sessionHistoryResponse.data.sessions[0]; // Most recent session
              setLatestCognitiveState(latestSession.cognitive_state);
            }
          } catch (cognitiveError) {
            console.warn('Could not load cognitive state:', cognitiveError);
          }
        }

        // Fetch analytics based on user tier - implementing Hook Model
        if (hasFeatureAccess('BASIC_PROGRESS_TRACKING')) {
          try {
            // Use basic analytics for Student tier
            const analyticsResponse = await axios.get('/api/analytics/basic?user_id=' + (userTier || 'guest'));
            setTaxonomyData(analyticsResponse.data.analytics?.taxonomy_progress);
            setAdaptationData(analyticsResponse.data.analytics?.session_summary);
          } catch (analyticsError) {
            console.warn('Basic analytics not available:', analyticsError);
            // Fallback to preview for upgrade encouragement
            const previewResponse = await axios.get('/api/analytics/preview?user_id=' + (userTier || 'guest'));
            setTaxonomyData(previewResponse.data.preview);
          }
        } else if (hasFeatureAccess('PROGRESS_TRACKING')) {
          try {
            // Use advanced analytics for Premium+ tiers
            const analyticsResponse = await axios.get('/api/analytics/advanced?user_id=' + (userTier || 'guest'));
            setTaxonomyData(analyticsResponse.data.analytics?.full_taxonomy_progress);
            setAdaptationData(analyticsResponse.data.analytics?.curiosity_metrics);
          } catch (analyticsError) {
            console.warn('Advanced analytics not available:', analyticsError);
          }
        } else {
          // Show preview for upgrade encouragement (Hook Model)
          try {
            const previewResponse = await axios.get('/api/analytics/preview?user_id=' + (userTier || 'guest'));
            setTaxonomyData(previewResponse.data.preview);
            setAdaptationData({ upgrade_required: true });
          } catch (previewError) {
            console.warn('Preview not available:', previewError);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };
    
    // Only load initial data
    fetchInitialData();
  }, [hasFeatureAccess, userTier]);

  // Sessions are loaded once on initial render - no polling needed
  // Users see fresh analytics data each time they visit the dashboard

  // Prepare chart data for taxonomy results - Hook Model implementation
  const taxonomyChartData = taxonomyData && !taxonomyData.preview ? {
    labels: Object.keys(taxonomyData),
    datasets: [
      {
        label: 'Average Score',
        data: Object.values(taxonomyData).map(level => level.average_score || 0),
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      },
    ],
  } : null;

  // Prepare engagement/session data for basic users
  const sessionChartData = adaptationData && adaptationData.count !== undefined ? {
    labels: ['This Week'],
    datasets: [
      {
        label: 'Sessions',
        data: [adaptationData.count],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      },
      {
        label: 'Minutes',
        data: [Math.round(adaptationData.total_time_minutes || 0)],
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      },
    ],
  } : null;

  // Advanced chart data for Premium+ users
  const advancedChartData = adaptationData && adaptationData.exploration_tendency !== undefined ? {
    labels: ['Exploration', 'Persistence', 'Questioning'],
    datasets: [
      {
        label: 'Curiosity Metrics',
        data: [
          adaptationData.exploration_tendency || 0,
          adaptationData.persistence_score || 0,
          adaptationData.questioning_frequency || 0
        ],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      },
    ],
  } : null;

  return (
    <div className="dashboard" style={{
      fontFamily: Branding.typography.fontFamily,
      color: Branding.colors.darkSlate,
      backgroundColor: Branding.colors.nearlyWhite,
      padding: Branding.spacing.lg,
      borderRadius: 0,
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      boxSizing: 'border-box',
      overflow: 'auto',
      position: 'relative'
    }}>
      {/* Preview Mode Badge for Guests - Stripe/Linear style */}
      {isGuest && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 1000,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '12px',
          padding: '12px 20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #F7931E, #E25822)',
            animation: 'pulse 2s infinite'
          }} />
          <span style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#1a1a1a',
            letterSpacing: '0.3px'
          }}>Preview Mode</span>
        </div>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: Branding.spacing.sm,
        marginBottom: Branding.spacing.xl,
        paddingBottom: Branding.spacing.md,
        borderBottom: `${Branding.borders.width.thin} solid ${Branding.colors.veryLightGray}`
      }}>
        <h1 style={{
          fontSize: Branding.typography.fontSize['2xl'],
          fontWeight: Branding.typography.fontWeight.bold,
          color: Branding.colors.deepPurple,
          margin: 0,
          minWidth: 0, // Allow text to shrink
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          <span style={{ 
            color: Branding.colors.vibrantOrange, 
            fontWeight: Branding.typography.fontWeight.bold
          }}>Tappy</span> Dashboard
        </h1>
      </div>

      {/* Guest Preview CTA - Stripe/Framer style */}
      {isGuest && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: Branding.spacing.xl,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)'
        }}>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '12px',
              lineHeight: '1.3'
            }}>
              See How Your Child's Learning Comes to Life
            </h2>
            <p style={{
              fontSize: '16px',
              opacity: 0.95,
              marginBottom: '24px',
              lineHeight: '1.6',
              maxWidth: '600px'
            }}>
              These previews show what you'll unlock with unlimited access. Get real-time insights into their understanding, track progress from memorization to critical thinking, and know exactly when they need help.
            </p>
            <button
              onClick={() => window.location.href = '/pricing'}
              style={{
                background: 'white',
                color: '#667eea',
                border: 'none',
                padding: '14px 32px',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
              }}
            >
              Unlock Full Analytics
            </button>
          </div>
          {/* Decorative gradient blob */}
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none'
          }} />
        </div>
      )}

      {loading ? (
        <div className="loading">Loading dashboard data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          <div className="card" style={{
            backgroundColor: Branding.colors.pureWhite,
            borderRadius: Branding.borders.radius.md,
            padding: Branding.spacing.lg,
            boxShadow: Branding.shadows.sm,
            marginBottom: Branding.spacing.xl
          }}>
            <h2 style={{
              fontSize: Branding.typography.fontSize.xl,
              fontWeight: Branding.typography.fontWeight.semibold,
              color: Branding.colors.darkSlate,
              marginBottom: Branding.spacing.md,
              display: 'flex',
              alignItems: 'center',
              gap: Branding.spacing.sm
            }}>
              <span style={{
                backgroundColor: Branding.colors.tealBlue,
                color: Branding.colors.pureWhite,
                width: '32px',
                height: '32px',
                borderRadius: Branding.borders.radius.full,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: Branding.typography.fontSize.sm,
                fontWeight: Branding.typography.fontWeight.bold
              }}>
                {sessions.length}
              </span>
              Active Tutoring Sessions
            </h2>
            
            {sessions.length === 0 ? (
              <div style={{
                backgroundColor: Branding.colors.offWhite,
                padding: Branding.spacing.lg,
                borderRadius: Branding.borders.radius.md,
                textAlign: 'center'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  margin: '0 auto',
                  marginBottom: Branding.spacing.md,
                  background: `linear-gradient(135deg, ${Branding.colors.tealBlue}, ${Branding.colors.primaryBlue})`,
                  borderRadius: Branding.borders.radius.md,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.6
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    background: Branding.colors.pureWhite,
                    borderRadius: Branding.borders.radius.sm
                  }} />
                </div>
                <p style={{
                  color: Branding.colors.slateGray,
                  fontWeight: Branding.typography.fontWeight.medium
                }}>
                  No active tutoring sessions
                </p>
                <Link to="/tutor" style={{
                  display: 'inline-block',
                  marginTop: Branding.spacing.md,
                  backgroundColor: Branding.colors.vibrantOrange,
                  color: Branding.colors.pureWhite,
                  padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
                  borderRadius: Branding.borders.radius.md,
                  textDecoration: 'none',
                  fontWeight: Branding.typography.fontWeight.medium,
                  boxShadow: Branding.shadows.sm
                }}>
                  Start Tutoring
                </Link>
              </div>
            ) : (
              <div className="sessions-list" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: Branding.spacing.md
              }}>
                {sessions.map(session => (
                  <div key={session.id} className="session-item" style={{
                    backgroundColor: Branding.colors.nearlyWhite,
                    borderRadius: Branding.borders.radius.md,
                    padding: Branding.spacing.md,
                    boxShadow: Branding.shadows.sm,
                    border: `${Branding.borders.width.thin} solid ${Branding.colors.veryLightGray}`
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: Branding.spacing.sm
                    }}>
                      <h3 style={{
                        fontSize: Branding.typography.fontSize.lg,
                        fontWeight: Branding.typography.fontWeight.semibold,
                        color: Branding.colors.deepPurple,
                        margin: 0
                      }}>
                        Session: {session.id}
                      </h3>
                      <span style={{
                        backgroundColor: Branding.colors.blueGreen,
                        color: Branding.colors.pureWhite,
                        padding: `${Branding.spacing.xs} ${Branding.spacing.sm}`,
                        borderRadius: Branding.borders.radius.full,
                        fontSize: Branding.typography.fontSize.xs,
                        fontWeight: Branding.typography.fontWeight.medium
                      }}>
                        {session.state?.length || 0} messages
                      </span>
                    </div>
                    
                    {/* Enhanced Cognitive State Display - Using New Analytics Data */}
                    <div className="session-state" style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: Branding.spacing.sm
                    }}>
                      <div className="state-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: Branding.spacing.md
                      }}>
                        <span style={{
                          width: '110px',
                          fontWeight: Branding.typography.fontWeight.medium,
                          color: Branding.colors.darkSlate,
                          fontSize: Branding.typography.fontSize.sm
                        }}>Knowledge:</span>
                        <div className="progress-bar" style={{
                          flex: 1,
                          height: '8px',
                          backgroundColor: Branding.colors.veryLightGray,
                          borderRadius: Branding.borders.radius.full,
                          overflow: 'hidden'
                        }}>
                          <div
                            className="progress-value"
                            style={{
                              width: `${Math.round((session.cognitive_state?.knowledge_level || 0) * 100)}%`,
                              height: '100%',
                              backgroundColor: Branding.colors.blueGreen,
                              borderRadius: Branding.borders.radius.full
                            }}
                          />
                        </div>
                        <span style={{
                          width: '40px',
                          textAlign: 'right',
                          fontWeight: Branding.typography.fontWeight.semibold,
                          fontSize: Branding.typography.fontSize.sm
                        }}>
                          {Math.round((session.cognitive_state?.knowledge_level || 0) * 100)}%
                        </span>
                      </div>
                      
                      <div className="state-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: Branding.spacing.md
                      }}>
                        <span style={{
                          width: '110px',
                          fontWeight: Branding.typography.fontWeight.medium,
                          color: Branding.colors.darkSlate,
                          fontSize: Branding.typography.fontSize.sm
                        }}>Engagement:</span>
                        <div className="progress-bar" style={{
                          flex: 1,
                          height: '8px',
                          backgroundColor: Branding.colors.veryLightGray,
                          borderRadius: Branding.borders.radius.full,
                          overflow: 'hidden'
                        }}>
                          <div
                            className="progress-value"
                            style={{
                              width: `${Math.round((session.cognitive_state?.engagement_score || 0) * 100)}%`,
                              height: '100%',
                              backgroundColor: Branding.colors.tealBlue,
                              borderRadius: Branding.borders.radius.full
                            }}
                          />
                        </div>
                        <span style={{
                          width: '40px',
                          textAlign: 'right',
                          fontWeight: Branding.typography.fontWeight.semibold,
                          fontSize: Branding.typography.fontSize.sm
                        }}>
                          {Math.round((session.cognitive_state?.engagement_score || 0) * 100)}%
                        </span>
                      </div>
                      
                      <div className="state-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: Branding.spacing.md
                      }}>
                        <span style={{
                          width: '110px',
                          fontWeight: Branding.typography.fontWeight.medium,
                          color: Branding.colors.darkSlate,
                          fontSize: Branding.typography.fontSize.sm
                        }}>Confusion:</span>
                        <div className="progress-bar" style={{
                          flex: 1,
                          height: '8px',
                          backgroundColor: Branding.colors.veryLightGray,
                          borderRadius: Branding.borders.radius.full,
                          overflow: 'hidden'
                        }}>
                          <div
                            className="progress-value"
                            style={{
                              width: `${Math.round((session.cognitive_state?.confusion_indicators || 0) * 100)}%`,
                              height: '100%',
                              backgroundColor: Branding.colors.vibrantOrange,
                              borderRadius: Branding.borders.radius.full
                            }}
                          />
                        </div>
                        <span style={{
                          width: '40px',
                          textAlign: 'right',
                          fontWeight: Branding.typography.fontWeight.semibold,
                          fontSize: Branding.typography.fontSize.sm
                        }}>
                          {Math.round((session.cognitive_state?.confusion_indicators || 0) * 100)}%
                        </span>
                      </div>
                      
                      {/* Show data source indicator */}
                      <div style={{
                        fontSize: Branding.typography.fontSize.xs,
                        color: Branding.colors.slateGray,
                        fontStyle: 'italic',
                        marginTop: Branding.spacing.xs
                      }}>
                        {latestCognitiveState ? 'Latest session analytics' : 'Session data'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Removed duplicate chart cards - functionality preserved in aquarium components */}
          
          {/* Learning Aquarium Ecosystem */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr', // Single column layout for mobile compatibility
            gap: Branding.spacing.md,
            marginBottom: Branding.spacing.xl,
            width: '100%'
          }}>
            {/* Bloom's Taxonomy Fish */}
            <BloomsTaxonomyFish 
              userId={userId} 
              userTier={userTier} 
              hasFeatureAccess={hasFeatureAccess} 
            />
            
            {/* Right Panel - Water Clarity + Ecosystem Health */}
            <div style={{
              display: 'grid',
              gridTemplateRows: '1fr 1fr',
              gap: Branding.spacing.md
            }}>
              {/* Water Clarity Indicator */}
              <WaterClarityIndicator 
                userId={userId} 
                userTier={userTier} 
                hasFeatureAccess={hasFeatureAccess} 
              />
              
              {/* Ecosystem Health Dashboard */}
              <EcosystemHealthDashboard 
                userId={userId} 
                userTier={userTier} 
                hasFeatureAccess={hasFeatureAccess} 
              />
            </div>
          </div>
          
          {/* Advanced Analytics Widgets */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', // Simplified responsive grid
            gap: Branding.spacing.md,
            marginBottom: Branding.spacing.xl,
            width: '100%'
          }}>
            {/* Session History Timeline */}
            <SessionHistoryTimeline 
              userId={userId} 
              userTier={userTier} 
              hasFeatureAccess={hasFeatureAccess} 
            />
            
            {/* Curiosity Radar Chart - Premium Feature */}
            <CuriosityRadarChart 
              userId={userId} 
              userTier={userTier} 
              hasFeatureAccess={hasFeatureAccess} 
            />
            
            {/* Cognitive State Trends */}
            <CognitiveStateTrends 
              userId={userId} 
              userTier={userTier} 
              hasFeatureAccess={hasFeatureAccess} 
            />
          </div>
          
          {/* Contextual Curiosity Insights - Appears when pedagogically relevant */}
          {adaptationData && adaptationData.exploration_tendency !== undefined && (
            <div style={{
              ...Branding.components.analytics.dashboard.container,
              background: 'linear-gradient(135deg, rgba(57, 19, 57, 0.02), rgba(61, 73, 84, 0.02))',
              border: `1px solid rgba(57, 19, 57, 0.1)`,
              marginBottom: Branding.spacing.xl,
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Sophisticated insight glow effect */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: `linear-gradient(90deg, ${Branding.colors.deepPurple}, ${Branding.colors.primaryBlue}, ${Branding.colors.deepPurple})`,
                opacity: 0.6
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <h3 style={{
                  fontSize: Branding.typography.fontSize.xl,
                  fontWeight: Branding.typography.fontWeight.semibold,
                  color: Branding.colors.darkSlate,
                  marginBottom: Branding.spacing.lg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: Branding.spacing.sm
                }}>
                  <div style={{
                    width: '4px',
                    height: '20px',
                    background: `linear-gradient(180deg, ${Branding.colors.deepPurple}, ${Branding.colors.primaryBlue})`,
                    borderRadius: '2px'
                  }} />
                  Curiosity Profile Analysis
                  {hasFeatureAccess('LEARNING_ANALYSIS') ? (
                    <span style={{
                      fontSize: Branding.typography.fontSize.xs,
                      background: Branding.colors.deepPurple,
                      color: Branding.colors.pureWhite,
                      padding: `2px 8px`,
                      borderRadius: Branding.borders.radius.full,
                      fontWeight: Branding.typography.fontWeight.medium
                    }}>
                      LIVE
                    </span>
                  ) : (
                    <span style={{
                      fontSize: Branding.typography.fontSize.xs,
                      background: Branding.colors.vibrantOrange,
                      color: Branding.colors.pureWhite,
                      padding: `2px 8px`,
                      borderRadius: Branding.borders.radius.full,
                      fontWeight: Branding.typography.fontWeight.medium
                    }}>
                      PREVIEW
                    </span>
                  )}
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', // Mobile-optimized metrics grid
                  gap: Branding.spacing.sm,
                  width: '100%'
                }}>
                  {/* Exploration Tendency */}
                  <div style={{
                    textAlign: 'center',
                    padding: Branding.spacing.md
                  }}>
                    <div style={{
                      width: '72px',
                      height: '72px',
                      margin: '0 auto',
                      marginBottom: Branding.spacing.sm,
                      background: `conic-gradient(${Branding.colors.primaryBlue} ${adaptationData.exploration_tendency || 0}%, ${Branding.colors.veryLightGray} 0%)`,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        backgroundColor: Branding.colors.pureWhite,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: Branding.typography.fontSize.sm,
                        fontWeight: Branding.typography.fontWeight.bold,
                        color: Branding.colors.primaryBlue
                      }}>
                        {Math.round(adaptationData.exploration_tendency || 0)}%
                      </div>
                    </div>
                    <h4 style={{
                      fontSize: Branding.typography.fontSize.sm,
                      fontWeight: Branding.typography.fontWeight.medium,
                      color: Branding.colors.darkSlate,
                      margin: 0
                    }}>
                      Exploration
                    </h4>
                  </div>
                  
                  {/* Persistence */}
                  <div style={{
                    textAlign: 'center',
                    padding: Branding.spacing.md
                  }}>
                    <div style={{
                      width: '72px',
                      height: '72px',
                      margin: '0 auto',
                      marginBottom: Branding.spacing.sm,
                      background: `conic-gradient(${Branding.colors.tealBlue} ${adaptationData.persistence_score || 0}%, ${Branding.colors.veryLightGray} 0%)`,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        backgroundColor: Branding.colors.pureWhite,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: Branding.typography.fontSize.sm,
                        fontWeight: Branding.typography.fontWeight.bold,
                        color: Branding.colors.tealBlue
                      }}>
                        {Math.round(adaptationData.persistence_score || 0)}%
                      </div>
                    </div>
                    <h4 style={{
                      fontSize: Branding.typography.fontSize.sm,
                      fontWeight: Branding.typography.fontWeight.medium,
                      color: Branding.colors.darkSlate,
                      margin: 0
                    }}>
                      Persistence
                    </h4>
                  </div>
                  
                  {/* Show more metrics for Expert users */}
                  {hasFeatureAccess('LEARNING_ANALYSIS') && (
                    <>
                      <div style={{
                        textAlign: 'center',
                        padding: Branding.spacing.md
                      }}>
                        <div style={{
                          width: '72px',
                          height: '72px',
                          margin: '0 auto',
                          marginBottom: Branding.spacing.sm,
                          background: `conic-gradient(${Branding.colors.blueGreen} ${adaptationData.questioning_frequency || 0}%, ${Branding.colors.veryLightGray} 0%)`,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <div style={{
                            width: '56px',
                            height: '56px',
                            backgroundColor: Branding.colors.pureWhite,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: Branding.typography.fontSize.sm,
                            fontWeight: Branding.typography.fontWeight.bold,
                            color: Branding.colors.blueGreen
                          }}>
                            {Math.round(adaptationData.questioning_frequency || 0)}%
                          </div>
                        </div>
                        <h4 style={{
                          fontSize: Branding.typography.fontSize.sm,
                          fontWeight: Branding.typography.fontWeight.medium,
                          color: Branding.colors.darkSlate,
                          margin: 0
                        }}>
                          Questioning
                        </h4>
                      </div>
                      
                      <div style={{
                        textAlign: 'center',
                        padding: Branding.spacing.md
                      }}>
                        <div style={{
                          width: '72px',
                          height: '72px',
                          margin: '0 auto',
                          marginBottom: Branding.spacing.sm,
                          background: `conic-gradient(${Branding.colors.deepPurple} ${Math.random() * 80 + 10}%, ${Branding.colors.veryLightGray} 0%)`,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <div style={{
                            width: '56px',
                            height: '56px',
                            backgroundColor: Branding.colors.pureWhite,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: Branding.typography.fontSize.sm,
                            fontWeight: Branding.typography.fontWeight.bold,
                            color: Branding.colors.deepPurple
                          }}>
                            {Math.round(Math.random() * 80 + 10)}%
                          </div>
                        </div>
                        <h4 style={{
                          fontSize: Branding.typography.fontSize.sm,
                          fontWeight: Branding.typography.fontWeight.medium,
                          color: Branding.colors.darkSlate,
                          margin: 0
                        }}>
                          Insight Depth
                        </h4>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Contextual Teaching Insight - Appears based on curiosity pattern */}
                {adaptationData.exploration_tendency > 60 && (
                  <div style={{
                    marginTop: Branding.spacing.lg,
                    padding: Branding.spacing.md,
                    background: 'linear-gradient(135deg, rgba(59, 130, 184, 0.1), rgba(107, 175, 181, 0.1))',
                    border: `1px solid rgba(59, 130, 184, 0.2)`,
                    borderRadius: Branding.borders.radius.md
                  }}>
                    <p style={{
                      fontSize: Branding.typography.fontSize.sm,
                      color: Branding.colors.darkSlate,
                      margin: 0,
                      fontStyle: 'italic'
                    }}>
                      🔬 <strong>Research Insight:</strong> High exploration tendency detected. Consider introducing more open-ended problems to leverage this natural curiosity drive.
                    </p>
                  </div>
                )}
                
                {!hasFeatureAccess('LEARNING_ANALYSIS') && (
                  <div style={{
                    marginTop: Branding.spacing.lg,
                    textAlign: 'center'
                  }}>
                    <Link to="/pricing" style={{
                      display: 'inline-block',
                      background: Branding.gradients.premiumGradient,
                      color: Branding.colors.pureWhite,
                      padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
                      borderRadius: Branding.borders.radius.md,
                      textDecoration: 'none',
                      fontSize: Branding.typography.fontSize.sm,
                      fontWeight: Branding.typography.fontWeight.medium
                    }}>
                      Unlock Full Curiosity Analysis
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Expert Learning Intelligence - Expert tier only */}
          <FeatureGate
            feature="LEARNING_ANALYSIS"
            userTier={userTier}
            user={{ role: userTier }}
            showUpgradeMessage={false}
          >
            <ExpertLearningIntelligence user={{ role: userTier }} userTier={userTier} />
          </FeatureGate>
          
          {/* Group Management - Only show for HOME plan users */}
          {(userTier === 'expert-family' || userTier === 'home' || hasFeatureAccess('GROUP_MANAGEMENT')) && (
            <GroupManagement
              user={user}
              hasFeatureAccess={(feature) => {
                // Check if user has expert-family/home tier or group management access
                if (feature === 'GROUP_MANAGEMENT') {
                  return userTier === 'expert-family' || userTier === 'home' || hasFeatureAccess(feature);
                }
                return hasFeatureAccess(feature);
              }}
            />
          )}
          
          {/* Bounties and Friends Dashboard Widgets */}
          <div className="dashboard-widgets-row" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', // Mobile-friendly widgets
            gap: Branding.spacing.md,
            marginBottom: Branding.spacing.xl,
            width: '100%'
          }}>
            {/* Friend Training Widget */}
            <FriendTrainingDashboardWidget 
              userTier={userTier} 
              hasFeatureAccess={hasFeatureAccess} 
              user={{ role: userTier }} 
            />
            
            {/* Bounty Widget */}
            <BountyDashboardWidget 
              userTier={userTier} 
              hasFeatureAccess={hasFeatureAccess} 
              user={{ role: userTier }} 
            />
          </div>
          
          {/* Premium features section - only visible to premium+ tiers */}
          <FeatureGate
            feature="ADVANCED_VISUALIZATIONS"
            userTier={userTier}
            showUpgradeMessage={false}
          >
            <div className="premium-features" style={{
              marginTop: Branding.spacing.xl
            }}>
              <h2 style={{
                color: Branding.colors.darkSlate,
                marginBottom: Branding.spacing.md,
                position: 'relative',
                display: 'inline-block'
              }}>
                Premium Features
                <span style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '-40px',
                  backgroundColor: Branding.colors.vibrantOrange,
                  color: Branding.colors.pureWhite,
                  padding: `${Branding.spacing.xs} ${Branding.spacing.sm}`,
                  borderRadius: Branding.borders.radius.full,
                  fontSize: Branding.typography.fontSize.xs,
                  transform: 'rotate(15deg)'
                }}>
                  PREMIUM
                </span>
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', // PWA-optimized premium features grid
                gap: Branding.spacing.lg,
                width: '100%'
              }}>
                <div className="premium-feature-card" style={{
                  backgroundColor: Branding.colors.pureWhite,
                  borderRadius: Branding.borders.radius.md,
                  padding: Branding.spacing.lg,
                  boxShadow: Branding.shadows.md
                }}>
                  <h3 style={{ color: Branding.colors.darkSlate, marginBottom: Branding.spacing.sm }}>
                    Advanced Visualizations
                  </h3>
                  <p style={{ color: Branding.colors.slateGray, marginBottom: Branding.spacing.md }}>
                    Interactive 3D visualizations for complex mathematical concepts
                  </p>
                  <div style={{
                    backgroundColor: Branding.colors.offWhite,
                    height: '150px',
                    borderRadius: Branding.borders.radius.md,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: `linear-gradient(135deg, ${Branding.colors.tealBlue}, ${Branding.colors.primaryBlue})`,
                      borderRadius: Branding.borders.radius.lg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: Branding.colors.pureWhite,
                        borderRadius: Branding.borders.radius.md
                      }} />
                    </div>
                  </div>
                </div>
                
                <FeatureGate
                  feature="LEARNING_PORTFOLIO"
                  userTier={userTier}
                  showUpgradeMessage={false}
                >
                  <div className="premium-feature-card" style={{
                    backgroundColor: Branding.colors.pureWhite,
                    borderRadius: Branding.borders.radius.md,
                    padding: Branding.spacing.lg,
                    boxShadow: Branding.shadows.md
                  }}>
                    <h3 style={{ color: Branding.colors.darkSlate, marginBottom: Branding.spacing.sm }}>
                      Learning Portfolio
                    </h3>
                    <p style={{ color: Branding.colors.slateGray, marginBottom: Branding.spacing.md }}>
                      Track and showcase your mathematical achievements
                    </p>
                    <div style={{
                      backgroundColor: Branding.colors.offWhite,
                      height: '150px',
                      borderRadius: Branding.borders.radius.md,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: `linear-gradient(135deg, ${Branding.colors.blueGreen}, ${Branding.colors.seaGreen})`,
                        borderRadius: Branding.borders.radius.lg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          background: Branding.colors.pureWhite,
                          borderRadius: Branding.borders.radius.md
                        }} />
                      </div>
                    </div>
                  </div>
                </FeatureGate>
                
                <FeatureGate
                  feature="GITHUB_BOUNTIES"
                  userTier={userTier}
                  showUpgradeMessage={false}
                >
                  <div className="premium-feature-card" style={{
                    backgroundColor: Branding.colors.pureWhite,
                    borderRadius: Branding.borders.radius.md,
                    padding: Branding.spacing.lg,
                    boxShadow: Branding.shadows.md
                  }}>
                    <h3 style={{ color: Branding.colors.darkSlate, marginBottom: Branding.spacing.sm }}>
                      GitHub Bounties
                    </h3>
                    <p style={{ color: Branding.colors.slateGray, marginBottom: Branding.spacing.md }}>
                      Solve real-world math problems and build your portfolio
                    </p>
                    <div style={{
                      backgroundColor: Branding.colors.offWhite,
                      height: '150px',
                      borderRadius: Branding.borders.radius.md,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: `linear-gradient(135deg, ${Branding.colors.vibrantOrange}, ${Branding.colors.goldenOrange})`,
                        borderRadius: Branding.borders.radius.lg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          background: Branding.colors.pureWhite,
                          borderRadius: Branding.borders.radius.md
                        }} />
                      </div>
                    </div>
                  </div>
                </FeatureGate>
              </div>
            </div>
          </FeatureGate>
          
          {/* Premium teaser banner for student tier users */}
          {userTier === 'student' && !hasFeatureAccess('ADVANCED_VISUALIZATIONS') && (
            <div className="premium-teaser" style={{
              marginTop: Branding.spacing.xl,
              backgroundColor: Branding.colors.paleBlue + '20', // Light transparency
              borderRadius: Branding.borders.radius.lg,
              padding: Branding.spacing.lg,
              border: `1px dashed ${Branding.colors.tealBlue}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <h3 style={{
                color: Branding.colors.darkSlate,
                marginBottom: Branding.spacing.md
              }}>
                Unlock Premium Features
              </h3>
              <p style={{
                color: Branding.colors.slateGray,
                marginBottom: Branding.spacing.lg,
                maxWidth: '600px'
              }}>
                Upgrade to Premium to access advanced visualizations, GitHub bounties, 
                learning portfolio, and higher-level thinking questions.
              </p>
              <Link to="/pricing" style={{
                backgroundColor: Branding.colors.vibrantOrange,
                color: Branding.colors.pureWhite,
                padding: `${Branding.spacing.sm} ${Branding.spacing.xl}`,
                borderRadius: Branding.borders.radius.md,
                textDecoration: 'none',
                fontWeight: Branding.typography.fontWeight.medium,
                boxShadow: Branding.shadows.md
              }}>
                View Premium Features
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Dashboard;